import { HISTORY_ACTION_MODE, useNodeConnectors } from '@candulabs/craft';
import { useMemo, useRef } from 'react';

import { CustomNodeData } from 'craft/types';
import { immerToES6 } from 'craft/utils';

import { useEditor } from './useEditor';

/**
 * This is an adaptiation of craft's use node. It has big modifications:
 *
 * 1. It doesnt rely on the `NodeContext` to figure out the nodeId, but rather uses a prop
 * 2. It changes `setProps` to drop the `immer` api and switches it to a standard es6 api.
 *    We prefer this because first it's idiomatic, second because it protect us from future
 *    api changes where Craft drops Immer or similar API changes occur.
 * 3. Adds a `setCustom` function to help setting custom data
 *
 * 4. It can handle cases where nodeId is invalid or node is not present
 * 5. Collecting function should handle nullable node
 */
type useEditorNode = {
  connectors: ReturnType<typeof useNodeConnectors>;
  setCss: (css: Record<string, unknown>) => any;
  setCustom: (custom: CustomNodeData) => any;
  setCustomWithoutHistory: (custom: CustomNodeData) => any;
  setProps: (props: any) => any;
};

// TODO(mat) ask Prev about proper ts type to reflect this change
function useEditorNode(nodeId: string): useEditorNode {
  const { connectors: editorConnectors, actions } = useEditor();

  const setCustom = useMemo(
    () => immerToES6((callback: any) => actions?.setCustom(nodeId, callback)),
    [actions, nodeId],
  ) as (custom: CustomNodeData) => any;
  const setCustomWithoutHistory = useMemo(
    () =>
      immerToES6((callback: any) =>
        actions?.setCustom(nodeId, callback, HISTORY_ACTION_MODE.IGNORE),
      ),
    [actions, nodeId],
  ) as (custom: CustomNodeData) => any;

  const setProps = useMemo(
    () =>
      immerToES6((callback: any) =>
        actions?.setProp(nodeId, callback, HISTORY_ACTION_MODE.THROTTLE, 500),
      ),
    [actions, nodeId],
  );

  // TODO fix custom typings
  const setCss = useMemo(
    () => (props) =>
      actions?.setCustom(nodeId, (custom: any) => {
        // eslint-disable-next-line no-param-reassign
        custom.css = {
          ...custom.css,
          ...props,
        };
      }),
    [actions, nodeId],
  ) as (css: any) => any;

  // Using a ref here so we don't have to add it as a dependency for `connectors` below
  const editorConnectorsRef = useRef(editorConnectors);

  // Get the standard Craft Node connectors, but derive it from our custom EventHandler
  const connectors = useMemo(
    () => ({
      connect: (el: HTMLElement) => editorConnectorsRef.current.connect(el, nodeId),
      drag: (el: HTMLElement) => editorConnectorsRef.current.drag(el, nodeId),
    }),
    [],
  ) as ReturnType<typeof useNodeConnectors>;

  return {
    connectors,
    setCss,
    setCustom,
    setCustomWithoutHistory,
    setProps,
  };
}

export default useEditorNode;
